import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import SelectField from 'components/select-field';
import ConfirmDialog from 'components/users/confirm-dialog';
import { TFunction } from 'i18next';
import React from 'react';
import { OrderProperty } from 'types/order';
import { AccountingInvolvedParty, AccountingType } from 'v2-types/order';

import { AddAndUpdatePartyLedgerEntry } from '../../../components/add-and-update-party-ledger-modal/add-and-update-party-ledger-entry';
import { TableEntries } from './components/table-entries';

const COARSE_POINTER = '@media (hover: none) and (pointer: coarse)';

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(1) },
  },
  mainContainer: {
    padding: theme.spacing(3),
    minWidth: 0,
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) },
  },
  title: { marginBottom: theme.spacing(2) },
  buttonAddEntry: {
    paddingLeft: theme.spacing(3),
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      paddingTop: theme.spacing(1.5),
    },
  },
  addEntryButton: {
    textTransform: 'none',
    justifyContent: 'center',
    alignItems: 'center',
    // DESIGN.md §9: 44px touch targets.
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minHeight: 44,
    },
    [COARSE_POINTER]: { minHeight: 44 },
  },
  tableContainer: {
    marginTop: theme.spacing(3),
    minWidth: 0,
    [theme.breakpoints.down('sm')]: { marginTop: theme.spacing(2) },
  },
  emptyEntriesContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100px',
    textAlign: 'center',
  },
  emptyEntriesTitle: {
    color: theme.palette.primary.light,
    fontWeight: 100,
  },
  backdrop: { zIndex: theme.zIndex.drawer + 1 },
}));

type EarnestAndCommissionsSceneProps = {
  t: TFunction;
  selectedProperty: OrderProperty;
  properties: OrderProperty[];
  selectProperty: (id: string) => void;
  purchasePriceEntries: AccountingInvolvedParty[];
  setPartyLedgerEntry: React.Dispatch<
    React.SetStateAction<AccountingInvolvedParty | undefined>
  >;
  setOpenPartyLedgerModal: React.Dispatch<React.SetStateAction<boolean>>;
  partyLedgerEntry: AccountingInvolvedParty | undefined;
  entryLedger: AccountingType | undefined;
  openPartyLedgerModal: boolean;
  pendingDeleteEntry: AccountingInvolvedParty | undefined;
  requestRemoveEntry: (entry: AccountingInvolvedParty) => void;
  cancelRemoveEntry: () => void;
  confirmRemoveEntry: () => void;
  isLoading: boolean;
};

const getInitials = (name = '') => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0].toUpperCase())
  .join('');

const EarnestAndCommissionsScene = ({
  t,
  selectedProperty,
  properties,
  selectProperty,
  purchasePriceEntries,
  entryLedger,
  setOpenPartyLedgerModal,
  setPartyLedgerEntry,
  partyLedgerEntry,
  openPartyLedgerModal,
  pendingDeleteEntry,
  requestRemoveEntry,
  cancelRemoveEntry,
  confirmRemoveEntry,
  isLoading,
}: EarnestAndCommissionsSceneProps) => {
  const classes = useStyles();
  const theme = useTheme();
  // Phone breakpoint (<600px); MUI v4 `down('sm')` is <960px.
  const isMobile = !useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  return (
    <Box className={classes.rootContainer}>
      <Paper className={classes.mainContainer}>
        <Typography variant="h6" className={classes.title}>
          {t('earnestAndCommissions:earnest-and-commissions')}
        </Typography>
        <Grid container>
          <Grid item xs={12} sm={6}>
            <SelectField
              label={t('orders:propertiesLabel')}
              value={selectedProperty._id}
              data={properties.map((property) => ({
                _id: property._id,
                address: property.address.address,
              }))}
              dataKey="_id"
              dataValue="_id"
              dataText="address"
              handleChange={(e) => selectProperty(e.target.value)}
            />
          </Grid>
          {selectedProperty._id !== '' && (
            <Grid item xs={12} sm={6} className={classes.buttonAddEntry}>
              <Button
                color="secondary"
                className={classes.addEntryButton}
                disabled={isLoading}
                startIcon={(
                  <FontAwesomeIcon
                    icon="plus"
                    color="secondary"
                    style={{ fontSize: '0.875rem' }}
                  />
                )}
                onClick={() => setOpenPartyLedgerModal(true)}
              >
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {t('accounting:add-entry')}
                </Typography>
              </Button>
            </Grid>
          )}

          <Grid item xs={12} className={classes.tableContainer}>
            {purchasePriceEntries.length > 0 ? (
              <TableEntries
                setOpenPartyLedgerModal={setOpenPartyLedgerModal}
                setPartyLedgerEntry={setPartyLedgerEntry}
                purchasePriceEntries={purchasePriceEntries}
                t={t}
                onRemoveOrderPartyEntry={requestRemoveEntry}
                isMobile={isMobile}
                disabled={isLoading}
              />
            ) : (
              <Box className={classes.emptyEntriesContainer}>
                <Typography variant="h6" className={classes.emptyEntriesTitle}>
                  {t('earnestAndCommissions:empty-entries')}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
        {entryLedger && openPartyLedgerModal && (
          <AddAndUpdatePartyLedgerEntry
            kindAllowedAssociationList={['earnest', 'commission', 'option']}
            entryLedger={entryLedger}
            partyLedgerEntry={partyLedgerEntry}
            handleClose={() => {
              setPartyLedgerEntry(undefined);
              setOpenPartyLedgerModal(false);
            }}
            hideFields={{
              at_closing_amount: true,
              at_closing_percent: true,
              before_closing_amount: true,
              before_closing_percent: true
            }}
          />
        )}
        {pendingDeleteEntry && (
          <ConfirmDialog
            open
            confirm={false}
            fullScreen={isMobile}
            isLoading={isLoading}
            title={t('earnestAndCommissions:delete-entry-title')}
            name={pendingDeleteEntry.name}
            initials={getInitials(pendingDeleteEntry.name)}
            description={t('earnestAndCommissions:delete-entry-description')}
            confirmButtonText={t('common:delete')}
            handleConfirm={confirmRemoveEntry}
            handleCancel={cancelRemoveEntry}
          />
        )}
      </Paper>
      <Backdrop className={classes.backdrop} open={isLoading && !pendingDeleteEntry}>
        <CircularProgress color="secondary" />
      </Backdrop>
    </Box>
  );
};

export default EarnestAndCommissionsScene;
